import { LiveDot } from "@/components/live-dot";
import { EmptyState } from "@/components/empty-state";
import { formatRelative } from "@/lib/format";
import { cn } from "@/lib/cn";
import { Radio } from "lucide-react";
import type { EventSummary } from "@/api/types";

interface Props {
  items: EventSummary[];
  selectedId?: string;
  onSelect(id: string): void;
}

/** Presentational live-updating list of recent events, newest on top. */
export function StreamList({ items, selectedId, onSelect }: Props) {
  if (items.length === 0) {
    return (
      <EmptyState
        icon={<Radio size={32} />}
        title="Waiting for events…"
        description="Events will appear here as soon as they arrive."
      />
    );
  }

  return (
    <div className="scroll-thin flex flex-col divide-y overflow-y-auto">
      {items.map((e, i) => {
        const active = e.id === selectedId;
        return (
          <button
            key={e.id}
            type="button"
            onClick={() => onSelect(e.id)}
            className={cn(
              "flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors",
              active ? "bg-accent" : "hover:bg-accent/60",
            )}
          >
            <LiveDot color="var(--brand)" live={i === 0} size={7} />
            <span className="min-w-0 flex-1 truncate font-mono text-[13px] font-medium text-foreground">
              {e.name}
            </span>
            <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
              {formatRelative(e.received_at)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
